import { FLAG_ROLE_LABELS, SITE_ROLES, formatFlagInstant } from './flagSystem';
import { flagApplicabilityLabel, flagApplicabilityValue, hasFlagApplicability } from './flagApplicability';

export const AUDIT_EMPTY = '\u2014';
const missing = 'Not recorded';
const unavailable = 'Unavailable';
const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const dayOrder = [1, 2, 3, 4, 5, 6, 0];
const labels = {
  name: 'Flag name', description: 'Description',
  junioraction: 'Junior Local User action', dutymanageraction: 'Duty Manager action', senioraction: 'Senior Local User action',
  blockalltime: 'Block access all the time', active: 'Flag active', enabled: 'Flag System enabled',
  times: 'Allowed days and times', permissions: 'Role permissions', accesspointid: 'Access point ID',
  grantedatutc: 'Override granted at', expiresatutc: 'Override expires at',
  flagid: 'Flag ID', memberflagid: 'Member assignment ID', memberid: 'Member ID',
  applyonentry: 'Applies on entry', applyonexit: 'Applies on exit',
  customerid: 'Customer ID', locationid: 'Location ID', userid: 'Acting account ID', revision: 'Revision',
};
const object = value => Boolean(value && typeof value === 'object' && !Array.isArray(value));
const index = value => Object.entries(value).reduce((entries, [key, entry]) => {
  entries[key.toLowerCase()] = entry;
  return entries;
}, Object.create(null));
const label = key => Object.prototype.hasOwnProperty.call(labels, key.toLowerCase()) ? labels[key.toLowerCase()] :
  key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[_-]/g, ' ');
const clockSeconds = clock => {
  const [hour, minute, second = 0] = clock.split(':').map(Number);
  return hour * 3600 + minute * 60 + second;
};
const clockLabel = clock => /^\d{2}:\d{2}:00$/.test(clock) ? clock.slice(0, 5) : clock;

export const auditDateOrdinal = value => {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value) || value.startsWith('0000-')) return null;
  const date = new Date(`${value}T00:00:00Z`);
  return Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== value ? null : date.getTime() / 86400000;
};
export const defaultAuditFilters = (now = new Date()) => {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(now);
  const part = type => parts.find(item => item.type === type).value;
  const toDate = `${part('year')}-${part('month')}-${part('day')}`;
  const fromDate = new Date((auditDateOrdinal(toDate) - 29) * 86400000).toISOString().slice(0, 10);
  return { fromDate, toDate, action: '', memberId: '', flagId: null, locationId: null };
};
export const validateAuditFilters = filters => {
  const errors = {};
  const from = auditDateOrdinal(filters.fromDate), to = auditDateOrdinal(filters.toDate);
  if (from === null) errors.fromDate = 'Enter a valid British start date.';
  if (to === null) errors.toDate = 'Enter a valid British end date.';
  if (from !== null && to !== null) {
    if (to < from) errors.toDate = 'End date must be on or after the start date.';
    else if (to - from + 1 > 366) errors.toDate = 'Choose a range of at most 366 calendar days.';
  }
  for (const field of ['flagId', 'locationId']) {
    if (filters[field] !== null && filters[field] !== undefined && (!Number.isSafeInteger(filters[field]) || filters[field] <= 0)) {
      errors[field] = `Choose a valid ${field === 'flagId' ? 'flag' : 'location'}.`;
    }
  }
  return errors;
};
export const formatAuditInstant = value => typeof value === 'string' && /(?:Z|[+-]\d{2}:\d{2})$/i.test(value) &&
  !Number.isNaN(new Date(value).getTime()) ? formatFlagInstant(value) : 'Time unavailable';
export const auditActorLabel = audit => audit.actorName || 'Deleted or unavailable account';
export const auditActionLabel = audit => audit.actionLabel || 'Action label unavailable';
export const auditRoleLabel = audit => audit.roleLabel || 'Role label unavailable';

const valueText = (value, key, warnings) => {
  if (value === undefined) return missing;
  if (value === null) return AUDIT_EMPTY;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') {
    if (key.endsWith('utc')) {
      const text = formatAuditInstant(value);
      if (text === 'Time unavailable') warnings.add('Some recorded timestamps are malformed or do not include a time zone.');
      return text;
    }
    return value === '' ? '(empty)' : value;
  }
  if (Array.isArray(value) && value.every(item => item === null || ['string', 'number', 'boolean'].includes(typeof item))) {
    return value.length ? value.map(item => valueText(item, '', warnings)).join(', ') : 'None';
  }
  warnings.add('Some recorded fields have an unsupported structure and could not be displayed.');
  return unavailable;
};
const schedule = (value, warnings) => {
  if (value === undefined) return undefined;
  if (!Array.isArray(value)) {
    warnings.add('Recorded allowed days and times are malformed.');
    return null;
  }
  const result = new Map();
  for (const entry of value) {
    const time = object(entry) ? index(entry) : {};
    const validClock = clock => typeof clock === 'string' && /^(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d(?:\.\d+)?)?$|^24:00(?::00)?$/.test(clock);
    if (!Number.isInteger(time.dayofweek) || time.dayofweek < 0 || time.dayofweek > 6 ||
        !validClock(time.start) || !validClock(time.end) || clockSeconds(time.start) >= clockSeconds(time.end)) {
      warnings.add('Recorded allowed days and times are malformed.');
      return null;
    }
    const range = `${clockLabel(time.start)}\u2013${clockLabel(time.end)}`;
    result.set(time.dayofweek, [...(result.get(time.dayofweek) || []), range].sort());
  }
  return result;
};
const permissions = (value, warnings) => {
  if (value === undefined) return undefined;
  if (!Array.isArray(value)) {
    warnings.add('Recorded role permissions are malformed.');
    return null;
  }
  const result = new Map();
  for (const entry of value) {
    const permission = object(entry) ? index(entry) : {};
    if (typeof permission.role !== 'string' || !permission.role || result.has(permission.role) ||
        ['canoverride', 'canaddremove'].some(key => permission[key] !== undefined && typeof permission[key] !== 'boolean')) {
      warnings.add('Recorded role permissions are malformed or contain duplicate roles.');
      return null;
    }
    result.set(permission.role, permission);
    if (permission.canoverride === undefined || permission.canaddremove === undefined) {
      warnings.add('Some recorded role permission values were not supplied. Missing values are not treated as permission grants or denials.');
    }
  }
  return result;
};

export const auditChanges = (oldValues, newValues) => {
  const warnings = new Set();
  const snapshot = (value, side) => {
    if (value === null || value === undefined) return null;
    if (!object(value)) {
      warnings.add(`The recorded ${side} configuration is malformed. Available event details are shown without inventing missing values.`);
      return null;
    }
    return index(value);
  };
  const before = snapshot(oldValues, 'before'), after = snapshot(newValues, 'after');
  const oldMalformed = oldValues !== null && oldValues !== undefined && !object(oldValues);
  const newMalformed = newValues !== null && newValues !== undefined && !object(newValues);
  const mode = !before && !after ? 'none' : oldValues === null && after ? 'created' : newValues === null && before ? 'removed' : 'updated';
  const rows = [];
  const add = (key, text, previous, next, equal = previous === next) => {
    if (mode !== 'updated' || !equal) rows.push({ key, label: text, before: previous, after: next });
  };
  const keys = new Set([...Object.keys(before || {}), ...Object.keys(after || {})]);
  const fieldNames = Object.fromEntries([oldValues, newValues].filter(object)
    .flatMap(value => Object.keys(value).map(key => [key.toLowerCase(), key])));
  const scopeOf = values => values && ({ applyOnEntry: values.applyonentry, applyOnExit: values.applyonexit });
  const completeOrAbsent = values => !values ||
    (values.applyonentry === undefined && values.applyonexit === undefined) || hasFlagApplicability(scopeOf(values));
  const combinedScope = (keys.has('applyonentry') || keys.has('applyonexit')) &&
    completeOrAbsent(before) && completeOrAbsent(after);
  if (combinedScope) {
    if ([before, after].some(values => hasFlagApplicability(scopeOf(values)) && !flagApplicabilityValue(scopeOf(values)))) {
      warnings.add('A recorded applicability configuration selects neither direction.');
    }
    const text = (values, malformed) => malformed ? unavailable : !hasFlagApplicability(scopeOf(values)) ? missing :
      flagApplicabilityLabel(scopeOf(values));
    add('applicability', 'Applies to', text(before, oldMalformed), text(after, newMalformed));
  } else if (keys.has('applyonentry') || keys.has('applyonexit')) {
    warnings.add('Some recorded applicability values are missing or malformed. Missing historical values are not assumed to mean both directions.');
  }
  for (const key of keys) {
    if (combinedScope && ['applyonentry', 'applyonexit'].includes(key)) continue;
    const previous = before ? before[key] : undefined, next = after ? after[key] : undefined;
    if (key === 'times') {
      const oldDays = oldMalformed ? null : schedule(previous, warnings), newDays = newMalformed ? null : schedule(next, warnings);
      const days = dayOrder.filter(day => (oldDays && oldDays.has(day)) || (newDays && newDays.has(day)));
      const text = (daysMap, day) => daysMap === undefined ? missing : daysMap === null ? unavailable :
        daysMap.has(day) ? daysMap.get(day).join(', ') : 'None';
      if (!days.length) add(key, labels[key], text(oldDays), text(newDays));
      else for (const day of days) add(`${key}-${day}`, `${labels[key]} - ${weekdays[day]}`, text(oldDays, day), text(newDays, day));
    } else if (key === 'permissions') {
      const oldRoles = oldMalformed ? null : permissions(previous, warnings), newRoles = newMalformed ? null : permissions(next, warnings);
      const roles = [...new Set([...(oldRoles ? oldRoles.keys() : []), ...(newRoles ? newRoles.keys() : [])])]
        .sort((a, b) => (SITE_ROLES.includes(a) ? SITE_ROLES.indexOf(a) : 3) - (SITE_ROLES.includes(b) ? SITE_ROLES.indexOf(b) : 3) || a.localeCompare(b));
      const text = (roleMap, role, property) => roleMap === undefined ? missing : roleMap === null ? unavailable :
        !roleMap.has(role) ? 'No role entry' : valueText(roleMap.get(role)[property], '', warnings);
      if (!roles.length) add(key, labels[key], oldRoles === undefined ? missing : oldRoles === null ? unavailable : 'None',
        newRoles === undefined ? missing : newRoles === null ? unavailable : 'None');
      for (const role of roles) for (const [property, name] of [['canoverride', 'Override'], ['canaddremove', 'Add/Remove']]) {
        add(`${key}-${role}-${property}`, `${SITE_ROLES.includes(role) ? FLAG_ROLE_LABELS[role] : role} - ${name}`, text(oldRoles, role, property), text(newRoles, role, property));
      }
    } else {
      add(key, label(fieldNames[key]), oldMalformed ? unavailable : valueText(previous, key, warnings),
        newMalformed ? unavailable : valueText(next, key, warnings), previous === next);
    }
  }
  return { mode, rows, warnings: [...warnings] };
};
